import { useCallback, useEffect, useRef, useState } from "react";
import {
  GOOGLE_AUTH_ERROR_TYPE,
  GOOGLE_AUTH_SUCCESS_TYPE,
  googleAuthPopupMessageSchema,
  type GoogleAuthPopupMessage,
} from "@multi-chat/shared";
import { API_BASE_URL } from "../../api/client.js";
import { googleStartUrl } from "../../api/auth-api.js";
import { openCenteredPopup } from "../../lib/open-centered-popup.js";

export type GooglePopupStatus =
  | { kind: "idle" }
  | { kind: "opening" }
  | { kind: "blocked" }
  | { kind: "closed" }
  | { kind: "success" }
  | { kind: "error"; code: string };

interface UseGooglePopupResult {
  status: GooglePopupStatus;
  start: () => void;
  reset: () => void;
}

const POPUP_NAME = "multi_chat_google_login";
const POPUP_SIZE = { width: 500, height: 640 };

function originMatches(origin: string): boolean {
  try {
    return new URL(origin).origin === new URL(API_BASE_URL).origin;
  } catch {
    return false;
  }
}

export function useGooglePopup(onSuccess: () => void): UseGooglePopupResult {
  const [status, setStatus] = useState<GooglePopupStatus>({ kind: "idle" });
  const popupRef = useRef<Window | null>(null);
  const closedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const clearClosedTimer = useCallback(() => {
    if (closedTimerRef.current) {
      clearInterval(closedTimerRef.current);
      closedTimerRef.current = null;
    }
  }, []);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (!originMatches(event.origin)) return;
      const parsed = googleAuthPopupMessageSchema.safeParse(event.data);
      if (!parsed.success) return;

      const message: GoogleAuthPopupMessage = parsed.data;
      if (message.type === GOOGLE_AUTH_SUCCESS_TYPE) {
        clearClosedTimer();
        window.removeEventListener("message", handleMessage);
        popupRef.current = null;
        setStatus({ kind: "success" });
        onSuccessRef.current();
      } else if (message.type === GOOGLE_AUTH_ERROR_TYPE) {
        clearClosedTimer();
        window.removeEventListener("message", handleMessage);
        // The popup completion script in GooglePopupCallback already self-closes via window.close().
        // Parent-side popup closure is redundant and blocked by browser COOP policies.
        popupRef.current = null;
        setStatus({ kind: "error", code: message.code });
      }
    },
    [clearClosedTimer],
  );

  const reset = useCallback(() => {
    clearClosedTimer();
    window.removeEventListener("message", handleMessage);
    popupRef.current?.close();
    popupRef.current = null;
    setStatus({ kind: "idle" });
  }, [clearClosedTimer, handleMessage]);

  const start = useCallback(() => {
    setStatus({ kind: "opening" });
    window.addEventListener("message", handleMessage);

    const url = googleStartUrl("popup", window.location.pathname + window.location.search);
    const popup = openCenteredPopup(url, POPUP_NAME, POPUP_SIZE);

    if (!popup) {
      window.removeEventListener("message", handleMessage);
      setStatus({ kind: "blocked" });
      return;
    }

    popupRef.current = popup;

    // Detect a popup that closes before sending a success/error message.
    clearClosedTimer();
    closedTimerRef.current = setInterval(() => {
      if (popup.closed) {
        clearClosedTimer();
        window.removeEventListener("message", handleMessage);
        popupRef.current = null;
        setStatus((current) =>
          current.kind === "success" || current.kind === "error"
            ? current
            : { kind: "closed" },
        );
      }
    }, 500);
  }, [handleMessage, clearClosedTimer]);

  useEffect(() => {
    return () => {
      // Do not close the popup here. Strict Mode remounts and parent
      // re-renders must not dismiss an in-progress Google account chooser.
      // Success/error handlers and reset() own intentional closes.
      clearClosedTimer();
      window.removeEventListener("message", handleMessage);
    };
  }, [clearClosedTimer, handleMessage]);

  return { status, start, reset };
}
